import { useState, useEffect } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Download, 
  Eye, 
  Globe, 
  Building2, 
  Loader2, 
  CheckCircle,
  AlertTriangle,
  Palette,
  Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { TemplateSetService, TemplateSet, TemplateSetItem } from '@/services/templateSetService';

interface TemplateSetLoaderProps {
  onTemplatesLoaded?: () => void;
}

export function TemplateSetLoader({ onTemplatesLoaded }: TemplateSetLoaderProps) {
  const { currentTenant } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [templateSets, setTemplateSets] = useState<TemplateSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSetId, setLoadingSetId] = useState<string | null>(null);
  const [previewItems, setPreviewItems] = useState<TemplateSetItem[]>([]);
  const [previewingSetId, setPreviewingSetId] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTemplateSets();
    }
  }, [isOpen]);

  const fetchTemplateSets = async () => {
    try {
      setIsLoading(true);
      const sets = await TemplateSetService.getAvailableTemplateSets();
      setTemplateSets(sets);
    } catch (error) {
      console.error('Error fetching template sets:', error);
      toast.error('Failed to load template sets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadTemplateSet = async (templateSetId: string, templateSetName: string) => {
    try {
      setLoadingSetId(templateSetId);
      
      if (!currentTenant?.tenant_id) {
        toast.error('No active tenant context found. Please refresh the page and try again.');
        return;
      }
      
      await TemplateSetService.loadTemplateSet(templateSetId, currentTenant.tenant_id);
      toast.success(`"${templateSetName}" template set loaded successfully into ${currentTenant.company_name}!`);
      setIsOpen(false);
      onTemplatesLoaded?.();
    } catch (error: any) {
      console.error('Error loading template set:', error);
      toast.error(error.message || 'Failed to load template set');
    } finally {
      setLoadingSetId(null);
    }
  };

  const handlePreviewTemplateSet = async (templateSetId: string) => {
    if (previewingSetId === templateSetId) {
      setPreviewingSetId(null);
      setPreviewItems([]);
      return;
    }

    try {
      setIsLoadingPreview(true);
      setPreviewingSetId(templateSetId);
      const items = await TemplateSetService.previewTemplateSet(templateSetId);
      setPreviewItems(items);
    } catch (error) {
      console.error('Error previewing template set:', error);
      toast.error('Failed to preview template set');
      setPreviewingSetId(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'professional':
        return <Sparkles className="h-4 w-4" />;
      case 'friendly':
        return <Palette className="h-4 w-4" />;
      default:
        return <Building2 className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'professional':
        return 'bg-blue-100 text-blue-800';
      case 'friendly':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const globalSets = templateSets.filter(set => set.scope === 'global');
  const tenantSets = templateSets.filter(set => set.scope === 'tenant');

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Load Template Set
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Load Notification Template Set
          </DialogTitle>
          <DialogDescription>
            Choose a template set to replace your current notification templates. This will overwrite existing templates.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading template sets...</span>
          </div>
        ) : (
          <Tabs defaultValue="global" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="global" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Global Sets ({globalSets.length})
              </TabsTrigger>
              <TabsTrigger value="tenant" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Custom Sets ({tenantSets.length})
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[500px] mt-4">
              <TabsContent value="global" className="space-y-4">
                {globalSets.length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    No global template sets available
                  </div>
                ) : (
                  globalSets.map((set) => (
                    <Card key={set.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <CardTitle className="text-lg">{set.name}</CardTitle>
                              <Badge variant="secondary" className={getCategoryColor(set.category)}>
                                <span className="flex items-center gap-1">
                                  {getCategoryIcon(set.category)}
                                  {set.category}
                                </span>
                              </Badge>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <Globe className="h-3 w-3 mr-1" />
                                Global
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{set.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreviewTemplateSet(set.id)}
                              disabled={isLoadingPreview && previewingSetId === set.id}
                            >
                              {isLoadingPreview && previewingSetId === set.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                              Preview
                            </Button>
                            <Button
                              onClick={() => handleLoadTemplateSet(set.id, set.name)}
                              disabled={loadingSetId === set.id}
                              className="gap-2"
                            >
                              {loadingSetId === set.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                              Load Set
                            </Button>
                          </div>
                        </div>
                      </CardHeader>

                      {previewingSetId === set.id && previewItems.length > 0 && (
                        <CardContent className="pt-0">
                          <Alert className="mb-4">
                            <CheckCircle className="h-4 w-4" />
                            <AlertDescription>
                              Preview of {previewItems.length} notification templates in this set:
                            </AlertDescription>
                          </Alert>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                            {previewItems.map((item) => (
                              <div key={item.id} className="text-sm p-2 bg-muted rounded border">
                                <span className="font-medium">{item.notification_type}</span>
                                {item.description && (
                                  <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="tenant" className="space-y-4">
                {tenantSets.length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    <Building2 className="h-8 w-8 mx-auto mb-3 opacity-50" />
                    <p>No custom template sets created yet</p>
                    <p className="text-xs mt-1">Create custom sets by saving your current templates</p>
                  </div>
                ) : (
                  tenantSets.map((set) => (
                    <Card key={set.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <CardTitle className="text-lg">{set.name}</CardTitle>
                              <Badge variant="secondary" className={getCategoryColor(set.category)}>
                                <span className="flex items-center gap-1">
                                  {getCategoryIcon(set.category)}
                                  {set.category}
                                </span>
                              </Badge>
                              <Badge variant="outline">
                                <Building2 className="h-3 w-3 mr-1" />
                                Custom
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{set.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreviewTemplateSet(set.id)}
                              disabled={isLoadingPreview && previewingSetId === set.id}
                            >
                              {isLoadingPreview && previewingSetId === set.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                              Preview
                            </Button>
                            <Button
                              onClick={() => handleLoadTemplateSet(set.id, set.name)}
                              disabled={loadingSetId === set.id}
                              className="gap-2"
                            >
                              {loadingSetId === set.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                              Load Set
                            </Button>
                          </div>
                        </div>
                      </CardHeader>

                      {previewingSetId === set.id && previewItems.length > 0 && (
                        <CardContent className="pt-0">
                          <Alert className="mb-4">
                            <CheckCircle className="h-4 w-4" />
                            <AlertDescription>
                              Preview of {previewItems.length} notification templates in this set:
                            </AlertDescription>
                          </Alert>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                            {previewItems.map((item) => (
                              <div key={item.id} className="text-sm p-2 bg-muted rounded border">
                                <span className="font-medium">{item.notification_type}</span>
                                {item.description && (
                                  <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}

        <DialogFooter>
          <Alert className="flex-1">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Loading a template set will replace your current notification templates. This action cannot be undone.
            </AlertDescription>
          </Alert>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}